import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { 
  Bell, 
  BellOff, 
  Check, 
  CheckCheck, 
  Eye, 
  FileText, 
  Pen, 
  XCircle, 
  Clock, 
  Trash2,
  Loader2,
  Settings
} from "lucide-react";
import { useLanguage } from "@/components/language-provider";
import { getApiUrl } from "@/lib/config";
import { formatDistanceToNow } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { Link, useLocation } from "wouter";
import { AppLayout } from "@/components/app-layout";

interface Notification {
  id: string;
  type: string;
  signerName: string;
  signerEmail: string;
  documentName: string;
  documentId: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  readAt: string | null;
}

interface NotificationSettings {
  popupEnabled: boolean;
  notifyOnViewed: boolean;
  notifyOnSigned: boolean;
  notifyOnDeclined: boolean;
  notifyOnCompleted: boolean;
  notifyOnExpired: boolean;
}

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem("authToken");
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
};

export default function Notifications() {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [showSettings, setShowSettings] = useState(false);
  const [, setLocation] = useLocation();
  const [user, setUser] = useState<any>(null);
  
  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (!stored) { setLocation("/login"); return; }
    setUser(JSON.parse(stored));
  }, [setLocation]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    setLocation("/login");
  };

  // Fetch notifications
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ["/api/notifications"],
    queryFn: async () => {
      const res = await fetch(getApiUrl("/api/notifications"), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch notifications");
      return res.json();
    },
  });

  // Fetch settings
  const { data: settingsData } = useQuery({
    queryKey: ["/api/notifications/settings"],
    queryFn: async () => {
      const res = await fetch(getApiUrl("/api/notifications/settings"), {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to fetch settings");
      return res.json();
    },
  });

  const settings: NotificationSettings = settingsData?.data || {
    popupEnabled: true,
    notifyOnViewed: true,
    notifyOnSigned: true,
    notifyOnDeclined: true,
    notifyOnCompleted: true,
    notifyOnExpired: true,
  };

  const notifications: Notification[] = notificationsData?.data?.notifications || [];
  const unreadCount = notificationsData?.data?.unreadCount || 0;

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(getApiUrl(`/api/notifications/${id}/read`), {
        method: "PATCH",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to mark as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(getApiUrl("/api/notifications/read-all"), {
        method: "PATCH",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to mark all as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: t("notifications.allMarkedRead") || "All notifications marked as read" });
    },
  });

  // Clear all mutation
  const clearAllMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(getApiUrl("/api/notifications/clear-all"), {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Failed to clear notifications");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      toast({ title: t("notifications.allCleared") || "All notifications cleared" });
    },
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (newSettings: NotificationSettings) => {
      const res = await fetch(getApiUrl("/api/notifications/settings"), {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newSettings),
      });
      if (!res.ok) throw new Error("Failed to update settings");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/settings"] });
      toast({ title: t("notifications.settingsSaved") || "Settings saved" });
    },
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "DocumentViewed":
        return <Eye className="h-5 w-5 text-blue-500" />;
      case "DocumentSigned":
        return <Pen className="h-5 w-5 text-green-500" />;
      case "DocumentDeclined":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "DocumentCompleted":
        return <CheckCheck className="h-5 w-5 text-emerald-500" />;
      case "SignatureExpired":
        return <Clock className="h-5 w-5 text-orange-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case "DocumentViewed":
        return "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800";
      case "DocumentSigned":
        return "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800";
      case "DocumentDeclined":
        return "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800";
      case "DocumentCompleted":
        return "bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800";
      case "SignatureExpired":
        return "bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800";
      default:
        return "bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return formatDistanceToNow(date, {
      addSuffix: true,
      locale: language === "pt" ? ptBR : enUS,
    });
  };

  const handleSettingChange = (key: keyof NotificationSettings, value: boolean) => {
    updateSettingsMutation.mutate({
      ...settings,
      [key]: value,
    });
  };

  if (!user) return null;

  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/notifications">
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Bell className="h-8 w-8 text-primary" />
            {t("notifications.title") || "Notifications"}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t("notifications.subtitle") || "Stay updated on your document activities"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(!showSettings)}
          >
            <Settings className="h-4 w-4 mr-2" />
            {t("notifications.settings") || "Settings"}
          </Button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card className="mb-6 animate-in fade-in-50 slide-in-from-top-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t("notifications.notificationSettings") || "Notification Settings"}
            </CardTitle>
            <CardDescription>
              {t("notifications.settingsDesc") || "Configure how and when you receive notifications"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Popup Toggle */}
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div className="flex items-center gap-3">
                {settings.popupEnabled ? (
                  <Bell className="h-5 w-5 text-primary" />
                ) : (
                  <BellOff className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <Label className="text-base font-medium">
                    {t("notifications.popupNotifications") || "Popup Notifications"}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {t("notifications.popupDesc") || "Show notifications in real-time on any page"}
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.popupEnabled}
                onCheckedChange={(checked) => handleSettingChange("popupEnabled", checked)}
              />
            </div>

            <Separator />

            {/* Notification Type Filters */}
            <div className="space-y-4">
              <Label className="text-base font-medium">
                {t("notifications.notificationTypes") || "Notification Types"}
              </Label>
              
              <div className="grid gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">{t("notifications.whenViewed") || "When document is viewed"}</span>
                  </div>
                  <Switch
                    checked={settings.notifyOnViewed}
                    onCheckedChange={(checked) => handleSettingChange("notifyOnViewed", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Pen className="h-4 w-4 text-green-500" />
                    <span className="text-sm">{t("notifications.whenSigned") || "When document is signed"}</span>
                  </div>
                  <Switch
                    checked={settings.notifyOnSigned}
                    onCheckedChange={(checked) => handleSettingChange("notifyOnSigned", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm">{t("notifications.whenDeclined") || "When signature is declined"}</span>
                  </div>
                  <Switch
                    checked={settings.notifyOnDeclined}
                    onCheckedChange={(checked) => handleSettingChange("notifyOnDeclined", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCheck className="h-4 w-4 text-emerald-500" />
                    <span className="text-sm">{t("notifications.whenCompleted") || "When all signatures completed"}</span>
                  </div>
                  <Switch
                    checked={settings.notifyOnCompleted}
                    onCheckedChange={(checked) => handleSettingChange("notifyOnCompleted", checked)}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-orange-500" />
                    <span className="text-sm">{t("notifications.whenExpired") || "When signature expires"}</span>
                  </div>
                  <Switch
                    checked={settings.notifyOnExpired}
                    onCheckedChange={(checked) => handleSettingChange("notifyOnExpired", checked)}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {notifications.length > 0 && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                {unreadCount} {t("notifications.unread") || "unread"}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAllAsReadMutation.mutate()}
                disabled={markAllAsReadMutation.isPending}
              >
                <CheckCheck className="h-4 w-4 mr-2" />
                {t("notifications.markAllRead") || "Mark all read"}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => clearAllMutation.mutate()}
              disabled={clearAllMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {t("notifications.clearAll") || "Clear all"}
            </Button>
          </div>
        </div>
      )}

      {/* Notifications List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : notifications.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">
              {t("notifications.noNotifications") || "No notifications"}
            </h3>
            <p className="text-muted-foreground">
              {t("notifications.noNotificationsDesc") || "You'll see notifications here when signers interact with your documents"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <Card
              key={notification.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                !notification.isRead ? getNotificationColor(notification.type) : ""
              }`}
              onClick={() => {
                if (!notification.isRead) {
                  markAsReadMutation.mutate(notification.id);
                }
              }}
            >
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-sm ${!notification.isRead ? "font-semibold" : ""}`}>
                          {notification.message}
                        </p>
                        {notification.signerEmail && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {notification.signerEmail}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!notification.isRead && (
                          <div className="w-2 h-2 rounded-full bg-primary" />
                        )}
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                    {notification.documentId && (
                      <Link href={`/documents/${notification.documentId}`}>
                        <Button variant="link" size="sm" className="h-auto p-0 mt-2 text-xs">
                          <FileText className="h-3 w-3 mr-1" />
                          {t("notifications.viewDocument") || "View document"}
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
    </AppLayout>
  );
}
